import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Fade,
  Button
} from '@mui/material';
import { ExpandMore, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const FAQ = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState('panel1');
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const { t } = useLanguage();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const toggleShowAllFaqs = () => {
    setShowAllFaqs(!showAllFaqs);
  };

  const faqs = [
    {
      id: 'panel1',
      question: t('faq.questions.howMuchCost.question'),
      answer: t('faq.questions.howMuchCost.answer')
    },
    {
      id: 'panel2',
      question: t('faq.questions.howProtectPrivacy.question'),
      answer: t('faq.questions.howProtectPrivacy.answer')
    },
    {
      id: 'panel3',
      question: t('faq.questions.atsFriendly.question'),
      answer: t('faq.questions.atsFriendly.answer')
    },
    {
      id: 'panel4',
      question: t('faq.questions.canImport.question'),
      answer: t('faq.questions.canImport.answer')
    },
    {
      id: 'panel5',
      question: t('faq.questions.exportFormats.question'),
      answer: t('faq.questions.exportFormats.answer')
    },
    {
      id: 'panel6',
      question: t('faq.questions.needAccount.question'),
      answer: t('faq.questions.needAccount.answer')
    },
    {
      id: 'panel7',
      question: t('faq.questions.mobileDevices.question'),
      answer: t('faq.questions.mobileDevices.answer')
    },
    {
      id: 'panel8',
      question: t('faq.questions.howManyResumes.question'),
      answer: t('faq.questions.howManyResumes.answer')
    },
    {
      id: 'panel9',
      question: t('faq.questions.canCustomize.question'),
      answer: t('faq.questions.canCustomize.answer')
    },
    {
      id: 'panel10',
      question: t('faq.questions.needHelp.question'),
      answer: t('faq.questions.needHelp.answer')
    }
  ];

  // Split the faqs into initial (visible) and additional (hidden) questions
  const initialFaqs = faqs.slice(0, 4);
  const additionalFaqs = faqs.slice(4);

  return (
    <Box
      id="faq"
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${tokens.colors.neutral[50]} 0%, ${tokens.colors.background.default} 100%)`,
        position: 'relative'
      }}
    >
      <Container maxWidth="md">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: 2
            }}
          >
            {t('faq.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: tokens.colors.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {t('faq.subtitle')}
          </Typography>
        </Box>

        {/* Initial FAQ Accordions */}
        <Box>
          {initialFaqs.map((faq, index) => (
            <Fade in timeout={800 + index * 100} key={faq.id}>
              <Accordion
                expanded={expanded === faq.id}
                onChange={handleChange(faq.id)}
                sx={{
                  backgroundColor: 'white',
                  border: `1px solid ${tokens.colors.borders.light}`,
                  borderRadius: `${tokens.radius.lg} !important`,
                  boxShadow: tokens.shadows.sm,
                  mb: 2,
                  '&:before': {
                    display: 'none'
                  },
                  '&.Mui-expanded': {
                    boxShadow: tokens.shadows.md,
                    borderColor: tokens.colors.primary.main
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMore 
                      sx={{ 
                        color: tokens.colors.primary.main,
                        fontSize: 28
                      }} 
                    />
                  }
                  sx={{
                    py: 2,
                    px: 3,
                    '& .MuiAccordionSummary-content': {
                      my: 1
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: tokens.typography.fontWeights.semibold,
                      color: tokens.colors.text.primary,
                      fontSize: { xs: '1rem', md: '1.1rem' }
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    px: 3,
                    pb: 3,
                    pt: 0
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: tokens.colors.text.secondary,
                      lineHeight: 1.6,
                      fontSize: '1rem'
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Fade>
          ))}
        </Box>

        {/* Toggle Button */}
        <Box sx={{ textAlign: 'center', mb: 4, mt: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={toggleShowAllFaqs}
            startIcon={showAllFaqs ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            sx={{
              borderColor: tokens.colors.primary.main,
              color: tokens.colors.primary.main,
              '&:hover': {
                backgroundColor: `${tokens.colors.primary.main}10`,
                borderColor: tokens.colors.primary.main,
              },
              px: 4,
              py: 1,
              borderRadius: tokens.radius.base,
              textTransform: 'none',
              fontWeight: tokens.typography.fontWeights.medium,
              fontSize: '1rem'
            }}
          >
            {showAllFaqs ? t('faq.showLess') : t('faq.showMore')}
          </Button>
        </Box>

        {/* Additional FAQ Accordions (hidden initially) */}
        {showAllFaqs && (
          <Box>
            {additionalFaqs.map((faq, index) => (
              <Fade in timeout={400 + index * 100} key={faq.id}>
                <Accordion
                  expanded={expanded === faq.id}
                  onChange={handleChange(faq.id)}
                  sx={{
                    backgroundColor: 'white',
                    border: `1px solid ${tokens.colors.borders.light}`,
                    borderRadius: `${tokens.radius.lg} !important`,
                    boxShadow: tokens.shadows.sm,
                    mb: 2,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      boxShadow: tokens.shadows.md,
                      borderColor: tokens.colors.primary.main
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMore 
                        sx={{ 
                          color: tokens.colors.primary.main,
                          fontSize: 28
                        }} 
                      />
                    }
                    sx={{
                      py: 2,
                      px: 3,
                      '& .MuiAccordionSummary-content': {
                        my: 1
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: tokens.typography.fontWeights.semibold,
                        color: tokens.colors.text.primary,
                        fontSize: { xs: '1rem', md: '1.1rem' }
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: tokens.colors.text.secondary,
                        lineHeight: 1.6,
                        fontSize: '1rem'
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Fade>
            ))}
          </Box>
        )}

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: tokens.typography.fontWeights.semibold,
              color: tokens.colors.text.primary,
              mb: 2,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}
          >
            {t('faq.finalCta.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: tokens.colors.text.secondary,
              fontSize: '1.1rem'
            }}
          >
            {t('faq.finalCta.subtitle')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQ; 